import React, { forwardRef } from "react";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import type { Chart as ChartJS, ChartOptions } from "chart.js";

interface Dataset {
  label: string;
  data: number[];
  borderColor?: string | string[];
  backgroundColor?: string | string[];
}

interface MISChartProps {
  labels: string[];
  datasets: Dataset[];
  type?: "bar" | "line" | "doughnut";
  heightClassName?: string;
  options?: ChartOptions;
}

export const MISChart = forwardRef<ChartJS, MISChartProps>(
  (
    {
      labels,
      datasets,
      type = "bar",
      heightClassName = "h-72",
      options: customOptions,
    },
    ref
  ) => {
  const defaultColor = "#229a3e";
  const defaultFill = "rgba(34, 154, 62, 0.35)";
  const data = {
    labels,
    datasets: datasets.map((d) => ({
      label: d.label,
      data: d.data,
      borderColor: d.borderColor ?? defaultColor,
      backgroundColor: d.backgroundColor ?? defaultFill,
      tension: 0,
      fill: type === "line",
    })),
  };

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(148, 163, 184, 0.15)" },
        ticks: {
          maxRotation: 30,
          minRotation: 0,
          color: "#64748b",
          font: { size: 11 },
        },
      },
      y: {
        grid: { color: "rgba(148, 163, 184, 0.15)" },
        beginAtZero: true,
        ticks: {
          color: "#64748b",
          font: { size: 11 },
        },
      },
    },
    ...(customOptions || {}),
  };

  return (
    <div className={`w-full ${heightClassName}`}>
      {type === "line" ? (
        <Line ref={ref as any} data={data} options={options as any} />
      ) : type === "doughnut" ? (
        <Doughnut ref={ref as any} data={data} options={options as any} />
      ) : (
        <Bar ref={ref as any} data={data} options={options as any} />
      )}
    </div>
  );
}
);

MISChart.displayName = "MISChart";

